import React, { useState, useEffect } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { formatDateString } from "@/lib/utils";
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { PlusCircle } from 'lucide-react';

interface EvaluationOption {
  id: string;
  label: string;
  session_date: string;
  sex: string;
  age_groups: string;
}

interface PlayerCheckin {
  id: string;
  player_id: string;
  session_id: string;
  checkin_time: string;
  jersey_number: string | null;
  players: {
    id: string;
    first_name: string;
    last_name: string;
    jersey_number: string | null;
  };
}

interface EvaluationListsProps {
  onAddPlayer: (player: { id: string; first_name: string; last_name: string; jersey_number: string | null; }) => Promise<void>;
}

const EvaluationLists: React.FC<EvaluationListsProps> = ({ onAddPlayer }) => {
  const [evaluationOptions, setEvaluationOptions] = useState<EvaluationOption[]>([]);
  const [selectedEvaluation, setSelectedEvaluation] = useState<string | null>(null);
  const [playerCheckins, setPlayerCheckins] = useState<PlayerCheckin[]>([]);
  const [playerTeams, setPlayerTeams] = useState<Record<string, { name: string; status: string }[]>>({});
  const [loading, setLoading] = useState(false);
  const [sortField, setSortField] = useState<keyof PlayerCheckin['players']>('first_name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const { user } = useAuth();

  useEffect(() => {
    const fetchEvaluationSessions = async () => {
      try {
        const { data: coachAgeGroups, error: ageGroupError } = await supabase
          .from("coach_age_groups")
          .select("age_group_id")
          .eq("coach_id", user?.id);

        if (ageGroupError) throw ageGroupError;
        const assignedAgeGroupIds = (coachAgeGroups || []).map((cag: any) => cag.age_group_id);

        if (assignedAgeGroupIds.length === 0) {
          setEvaluationOptions([]);
          return;
        }

        // First, fetch all session IDs that are linked to the coach's age groups
        const { data: sessionLinks, error: linkError } = await supabase
          .from("evaluation_session_age_groups")
          .select("evaluation_session_id")
          .in("age_group_id", assignedAgeGroupIds);

        if (linkError) throw linkError;
        const sessionIds = (sessionLinks || []).map((link: any) => link.evaluation_session_id);

        if (sessionIds.length === 0) {
          setEvaluationOptions([]);
          return;
        }

        // Fetch sessions where the coach has age group assignments
        const { data, error } = await supabase
          .from("evaluation_sessions")
          .select("id, session_date, evaluation_session_age_groups(age_group_id, age_groups(group_name, sex))")
          .in("id", sessionIds)
          .order("session_date", { ascending: true });
        
        if (error) throw error;
        
        const formattedOptions = data.map((session: any) => {
          const ageGroups = session.evaluation_session_age_groups
            .map((esag: { age_groups: { group_name: string; sex: string } }) => {
              const ageGroup = esag.age_groups;
              const sexLabel = ageGroup.sex === 'male' ? 'Boys' : ageGroup.sex === 'female' ? 'Girls' : 'Mixed';
              return ageGroup ? `${ageGroup.group_name} (${sexLabel})` : '';
            })
            .filter(Boolean)
            .join(', ');
          return {
            id: session.id,
            label: formatDateString(session.session_date, 'en-US', 'medium'),
            session_date: session.session_date,
            sex: '',
            age_groups: ageGroups
          };
        });
        
        setEvaluationOptions(formattedOptions);
      } catch (err) {
        console.error("Error fetching evaluation sessions:", err);
      }
    };
    
    fetchEvaluationSessions();
  }, []);

  useEffect(() => {
    if (selectedEvaluation) {
      fetchPlayerCheckins(selectedEvaluation);
    } else {
      setPlayerCheckins([]);
      setPlayerTeams({});
    }
  }, [selectedEvaluation]);

  const fetchPlayerCheckins = async (sessionId: string) => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('checkins')
        .select('id, player_id, session_id, checkin_time, jersey_number, players (id, first_name, last_name)')
        .eq('session_id', sessionId);

      if (error) {
        console.error('Error fetching player check-ins:', error);
        setPlayerCheckins([]);
        setPlayerTeams({});
      } else if (data) {
        setPlayerCheckins(data as unknown as PlayerCheckin[]);
        // Fetch team assignments for all checked-in players
        const playerIds = (data as any[]).map((checkin) => checkin.player_id);
        if (playerIds.length > 0) {
          const { data: teamData, error: teamError } = await supabase
            .from('team_players')
            .select('player_id, status, teams(name)')
            .in('player_id', playerIds);
          if (teamError) {
            console.error('Error fetching player teams:', teamError);
            setPlayerTeams({});
          } else if (teamData) {
            // Map player_id to array of { name, status }
            const teamsByPlayer: Record<string, { name: string; status: string }[]> = {};
            (teamData as any[]).forEach((row) => {
              if (!row.player_id || !row.teams) return;
              if (!teamsByPlayer[row.player_id]) teamsByPlayer[row.player_id] = [];
              teamsByPlayer[row.player_id].push({ name: row.teams.name, status: row.status });
            });
            setPlayerTeams(teamsByPlayer);
          }
        } else {
          setPlayerTeams({});
        }
      }
    } catch (err) {
      console.error('Unexpected error fetching player check-ins:', err);
      setPlayerCheckins([]);
      setPlayerTeams({});
    } finally {
      setLoading(false);
    }
  };

  const handleEvaluationChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedEvaluation(event.target.value);
  };

  const handleSort = (field: keyof PlayerCheckin['players']) => {
    setSortField(field);
    const newDirection = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortDirection(newDirection);

    const sortedPlayers = [...playerCheckins].sort((a, b) => {
      let valueA: string | number, valueB: string | number;

      if (field === 'jersey_number') {
        valueA = Number(a.jersey_number || 0);
        valueB = Number(b.jersey_number || 0);
      } else {
        valueA = a.players[field] || '';
        valueB = b.players[field] || '';
      }

      if (field === 'jersey_number') {
        return newDirection === 'asc' ? (valueA as number) - (valueB as number) : (valueB as number) - (valueA as number);
      } else {
        return newDirection === 'asc'
          ? String(valueA).localeCompare(String(valueB))
          : String(valueB).localeCompare(String(valueA));
      }
    });

    setPlayerCheckins(sortedPlayers);
  };

  const renderSortIcon = (field: keyof PlayerCheckin['players']) => {
    if (sortField === field) {
      return sortDirection === 'asc' ? <ChevronUp className="inline-block ml-1 h-4 w-4" /> : <ChevronDown className="inline-block ml-1 h-4 w-4" />;
    }
    return null;
  };

  const handleDownloadCSV = () => {
    if (!selectedEvaluation) return;

    const csvContent = [
      "Player Name,Jersey Number",
      ...playerCheckins.map((checkin) => {
        return `${checkin.players.first_name} ${checkin.players.last_name},${checkin.jersey_number || ''}`;
      }),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `evaluations_session_${selectedEvaluation}.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <div className="space-y-6">
      <div className="mb-4">
        <label htmlFor="evaluationSelect" className="block text-sm font-medium text-gray-700 mb-1">
          Previous Evaluations
        </label>
        <select
          id="evaluationSelect"
          value={selectedEvaluation || ""}
          onChange={handleEvaluationChange}
          className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="">Select an Evaluation</option>
          {evaluationOptions.map((option) => (
            <option key={option.id} value={option.id}>
              {`${option.label} - ${option.age_groups}`}
            </option>
          ))}
        </select>
      </div>
      <button
        onClick={handleDownloadCSV}
        disabled={!selectedEvaluation}
        className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded disabled:bg-gray-300"
      >
        Download CSV
      </button>
      {selectedEvaluation ? (
        <div className="mt-4">
          {loading ? (
            <div className="text-center py-10 text-gray-500">
              <p>Loading player check-ins...</p>
            </div>
          ) : (
            <div>
              {playerCheckins.length === 0 ? (
                <div className="text-center py-10 text-gray-500">
                  <p>No players checked in for this session</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full bg-white border border-gray-200">
                    <thead>
                      <tr className="bg-gray-100">
                        <th 
                          className="py-2 px-4 border-b text-left cursor-pointer hover:bg-gray-200"
                          onClick={() => handleSort('first_name')}
                        >
                          First Name {renderSortIcon('first_name')}
                        </th>
                        <th 
                          className="py-2 px-4 border-b text-left cursor-pointer hover:bg-gray-200"
                          onClick={() => handleSort('last_name')}
                        >
                          Last Name {renderSortIcon('last_name')}
                        </th>
                        <th 
                          className="py-2 px-4 border-b text-left cursor-pointer hover:bg-gray-200"
                          onClick={() => handleSort('jersey_number')}
                        >
                          Jersey Number {renderSortIcon('jersey_number')}
                        </th>
                        <th className="py-2 px-4 border-b text-left">
                          Teams (Status)
                        </th>
                        <th className="py-2 px-4 border-b text-left">
                          Consider
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {playerCheckins.map((checkin) => (
                        <tr key={checkin.id} className="hover:bg-gray-50">
                          <td className="py-2 px-4 border-b">{checkin.players.first_name}</td>
                          <td className="py-2 px-4 border-b">{checkin.players.last_name}</td>
                          <td className="py-2 px-4 border-b">{checkin.jersey_number || '-'}</td>
                          <td className="py-2 px-4 border-b">
                            {(playerTeams[checkin.player_id]?.length ?? 0) > 0 ?
                              playerTeams[checkin.player_id].map((team, idx) => (
                                <div key={idx}>
                                  <span className="font-medium">{team.name}</span>
                                  <span className="ml-1 text-xs text-gray-600">({team.status})</span>
                                </div>
                              )) : <span className="text-gray-400 italic">None</span>}
                          </td>
                          <td className="py-2 px-4 border-b">
                            <Button variant="outline" size="sm" onClick={async () => {
                              await onAddPlayer(checkin.players);
                              // Fetch new team assignment for this player to update state
                              const { data: newTeams, error: teamError } = await supabase
                                .from('team_players')
                                .select('status, teams(name)')
                                .eq('player_id', checkin.player_id);
                              if (!teamError && newTeams) {
                                setPlayerTeams(prev => ({
                                  ...prev,
                                  [checkin.player_id]: newTeams.map((row: any) => ({ name: row.teams.name, status: row.status })),
                                }));
                              }
                            }}>
                              <PlusCircle className="mr-1 h-4 w-4" /> Consider
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-10 text-gray-500">
          <p>Select an evaluation session to view player check-ins</p>
        </div>
      )}
    </div>
  );
};

export default EvaluationLists;
